import type { ReactNode } from 'react';
import {
  FUNNEL_BUCKET_LABELS,
  FUNNEL_BUCKETS,
  type FunnelBucket,
  type FunnelCounts,
  type HmBallInCourt,
} from '@nest-recruit/shared';
import { Chip } from '../../../components/Chip';
import { toneClass, type Tone } from '../../../components/Tone';
import styles from './HmBadges.module.css';

const BALL_LABELS: Readonly<Record<HmBallInCourt, string>> = {
  you: 'You',
  approver: 'Approver',
  hr: 'HR',
  candidate: 'Candidate',
  nobody: 'Paused',
};

/** "Waiting on you" is the one a manager must not miss, so it alone is attention. */
const BALL_TONES: Readonly<Record<HmBallInCourt, Tone>> = {
  you: 'attention',
  approver: 'progress',
  hr: 'progress',
  candidate: 'progress',
  nobody: 'hold',
};

/** Each bucket in the colour of its part of hiring, as on the pipeline board. */
const FUNNEL_BUCKET_TONES: Readonly<Record<FunnelBucket, Tone>> = {
  screening: 'screening',
  assessment: 'assessment',
  interview: 'interview',
  offer: 'offer',
  joined: 'done',
};

/** E16: "waiting on: you / HR / candidate" — explicit, never inferred from a status. */
export function WaitingOn({
  ball,
  name,
}: {
  readonly ball: HmBallInCourt;
  readonly name: string | null;
}): ReactNode {
  return (
    <span className={styles.waiting}>
      <span className={styles.waitingLabel}>Waiting on</span>
      <Chip tone={BALL_TONES[ball]} emphasis={ball === 'you' ? 'solid' : 'tonal'}>
        {BALL_LABELS[ball]}
      </Chip>
      {name && ball !== 'you' && <span className={styles.waitingName}>{name}</span>}
    </span>
  );
}

/**
 * E16's "stage funnel mini-bar". One segment per stage, each as wide as its share;
 * the counts are written underneath, because a bar alone cannot be read on a phone.
 */
export function FunnelBar({ funnel }: { readonly funnel: FunnelCounts }): ReactNode {
  const total = FUNNEL_BUCKETS.reduce((sum, bucket) => sum + funnel[bucket], 0);
  const summary = FUNNEL_BUCKETS.map((bucket) => `${FUNNEL_BUCKET_LABELS[bucket]} ${funnel[bucket]}`).join(', ');

  return (
    <figure className={styles.funnel} aria-label={`Candidates: ${summary}`}>
      <div className={styles.track} aria-hidden>
        {total === 0 ? (
          <span className={styles.trackEmpty} />
        ) : (
          FUNNEL_BUCKETS.filter((bucket) => funnel[bucket] > 0).map((bucket) => (
            <span
              key={bucket}
              className={`${toneClass} ${styles.segment}`}
              data-tone={FUNNEL_BUCKET_TONES[bucket]}
              // Measured geometry, not appearance: the share is the data (S2 convention).
              style={{ flexGrow: funnel[bucket] }}
            />
          ))
        )}
      </div>
      <ol className={styles.counts} aria-hidden>
        {FUNNEL_BUCKETS.map((bucket) => (
          <li key={bucket} className={styles.count} data-empty={funnel[bucket] === 0}>
            <span className={styles.countValue}>{funnel[bucket]}</span>
            <span className={styles.countLabel}>{FUNNEL_BUCKET_LABELS[bucket]}</span>
          </li>
        ))}
      </ol>
    </figure>
  );
}
